import { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { SchedulerTabsProps } from './Scheduler.types'
import {
  useScheduleProductionRequirements,
  useSchedulerTableHandlers,
} from './Scheduler.data'
import { SchedulerTanksRowType } from './Scheduler.types'
import { SchedulerTanksTable } from './SchedulerTanks.muiTable'
import { SchedulerTankEligibilityTable } from './SchedulerTankEligibility.muiTable'

/**
 * The holding tanks themselves, and which product each may hold.
 *
 * Two grids on one tab because both are week-invariant master data describing
 * the plant, and under V1 dedication they are the same list seen twice — one
 * row per vessel in each. The per-week planner input (opening stock, closing
 * target) is deliberately NOT here: it carries a week selector, and the
 * Resource Capacity Profile tab set the precedent that a per-week table earns
 * its own entry rather than becoming a second grid on a week-invariant tab.
 *
 * A schedule that declares no tank shows two empty grids and behaves exactly
 * as it did before the feature existed — the three tank tables are optional
 * server-side and read as empty rather than 404.
 */
export const ScheduleTanksView = ({
  schedulerId,
  iteration,
}: SchedulerTabsProps) => {
  const TANKS_API = 'tanks'
  const ELIGIBILITY_API = 'tank-eligibility'

  const {
    data: tanksResponse = { data: [], predicate: undefined },
    isLoading: isLoadingTanks,
  } = useScheduleProductionRequirements(schedulerId, TANKS_API, iteration)
  const { data: tanks = [], predicate: tanksPredicate } = tanksResponse

  const {
    data: eligibilityResponse = { data: [], predicate: undefined },
    isLoading: isLoadingEligibility,
  } = useScheduleProductionRequirements(schedulerId, ELIGIBILITY_API, iteration)
  const { data: eligibility = [], predicate: eligibilityPredicate } =
    eligibilityResponse

  const tanksHandlers = useSchedulerTableHandlers(
    schedulerId,
    TANKS_API,
    iteration
  )
  const eligibilityHandlers = useSchedulerTableHandlers(
    schedulerId,
    ELIGIBILITY_API,
    iteration
  )

  // Adding a vessel writes BOTH grids, because a tank with no eligibility row
  // holds nothing and is invisible to the engine — `resolve_tank_params` needs
  // the (tank, product) pair to know what the vessel carries. Splitting the two
  // halves across two manual steps is how you end up with a declared tank that
  // silently does nothing.
  //
  // Both writes are ordinary upserts: the Service merges `tanks` on `tankId`
  // and `tank-eligibility` on (tankId, productCode), inserting when nothing
  // matches, so no row has to exist first.
  const [addOpen, setAddOpen] = useState(false)
  const [newTankId, setNewTankId] = useState('')
  const [newProductCode, setNewProductCode] = useState('')
  const trimmedTank = newTankId.trim()
  const trimmedProduct = newProductCode.trim()
  const duplicate = (tanks as SchedulerTanksRowType[]).some(
    (t) => String(t.tankId).toLowerCase() === trimmedTank.toLowerCase()
  )

  const closeAdd = () => {
    setAddOpen(false)
    setNewTankId('')
    setNewProductCode('')
  }

  const handleAddTank = () => {
    if (!trimmedTank || duplicate) return
    tanksHandlers.updateTableData({
      0: {
        tankId: trimmedTank,
        capacity: null,
        UOM: null,
        maxHoldHours: null,
        // Explicitly true, not null: a kitchen run may add to what the vessel
        // already holds, which is the ordinary cross-week shape (the tank
        // opens part full and this week tops it up). A null would reach the
        // grid as an indeterminate checkbox, so the default is written out.
        allowAccumulation: true,
      },
    })
    if (trimmedProduct) {
      eligibilityHandlers.updateTableData({
        0: { tankId: trimmedTank, productCode: trimmedProduct },
      })
    }
    closeAdd()
  }

  return (
    <Box
      sx={{ height: 'calc(100vh - 240px)', minHeight: 400, overflowY: 'auto' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add Tank
        </Button>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
          Adds the vessel and the product it holds. Capacity and max hold can be
          filled in afterwards.
        </Typography>
      </Box>

      <Dialog open={addOpen} onClose={closeAdd} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.9rem' }}>Add Tank</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Tank ID"
              size="small"
              autoFocus
              value={newTankId}
              onChange={(e) => setNewTankId(e.target.value)}
              error={!!trimmedTank && duplicate}
              helperText={
                trimmedTank && duplicate
                  ? 'A tank with that id already exists'
                  : 'e.g. TANK02'
              }
            />
            <TextField
              label="Product Code held"
              size="small"
              value={newProductCode}
              onChange={(e) => setNewProductCode(e.target.value)}
              helperText="The intermediate this vessel holds. A tank with no product holds nothing and the engine ignores it."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleAddTank}
            size="small"
            variant="contained"
            disabled={!trimmedTank || duplicate}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 0.5 }}>
        Tanks
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1 }}>
        A tank is filled, held, then drained completely to a line or a second
        kitchen. Accumulate is on by default, so a kitchen run may add to what
        the vessel already holds as long as it is the same product, up to its
        Capacity; untick it and the vessel must be drawn empty and cleaned
        before it takes another batch. Maturity runs from the most recent run
        into the tank; the maximum hold runs from the first.
      </Typography>
      <SchedulerTanksTable
        data={tanks}
        isLoading={isLoadingTanks}
        updateTableData={tanksHandlers.updateTableData}
        uploadScheduleData={tanksHandlers.uploadScheduleData}
        isUploading={tanksHandlers.isUploading}
        isSaving={tanksHandlers.isSaving}
        updateRowsStatus={tanksHandlers.updateRowsStatus}
        predicate={tanksPredicate}
        deleteTableData={tanksHandlers.deleteTableData}
        isDeleting={tanksHandlers.isDeleting}
      />

      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, mt: 3, mb: 0.5 }}>
        Tank Eligibility
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1 }}>
        Which product each tank may hold — one row per tank, since a tank is
        dedicated to a single product in this version.
      </Typography>
      <SchedulerTankEligibilityTable
        data={eligibility}
        isLoading={isLoadingEligibility}
        updateTableData={eligibilityHandlers.updateTableData}
        uploadScheduleData={eligibilityHandlers.uploadScheduleData}
        isUploading={eligibilityHandlers.isUploading}
        isSaving={eligibilityHandlers.isSaving}
        updateRowsStatus={eligibilityHandlers.updateRowsStatus}
        predicate={eligibilityPredicate}
        deleteTableData={eligibilityHandlers.deleteTableData}
        isDeleting={eligibilityHandlers.isDeleting}
      />
    </Box>
  )
}
